import React, { useState, useEffect, useRef, useCallback } from "react";
import { Truck, MapPin, ArrowLeftRight } from "lucide-react";
import { FaTruckFront } from "react-icons/fa6";
import Cookies from "js-cookie";
import httpClient from "../../../../../../api/httpClient";
import { toast } from "react-toastify";
import FullScreenSlideUpModal from "../../../../../../components/ACS-Dashboard/ACS-Blocks/FullScreenSlideUpModal";
import notify from "../../../../../../components/ACS-Dashboard/ACS-Blocks/Toast";
import "react-toastify/dist/ReactToastify.css";
import Table1 from "../../../../../../components/ACS-Dashboard/ACS-Blocks/TableSecondary";
import { resourceSelectionApi } from "../../../../Resource-Selection/api/resourceSelectionApi";
import {
  buildRankedCarrierMap,
  ensureArray,
  normalizeRateShoppingResults,
  parseRateShoppingResponse,
} from "../rateShoppingUtils";
import {
  buildCarrierScoreBreakdown,
  CarrierScoreDetailsModal,
  findRankedCarrierRow,
  hasScoreBreakdownData,
  renderScoringExplanationBody,
} from "../../../../../../utils/carrierScoreDetailsUi";
import {
  extractSelectionOutcomeId,
  getCachedScoringExplanation,
  invalidateScoringExplanationCache,
  isUsefulScoringExplanation,
  pickLatestSelectionOutcomeId,
  setCachedScoringExplanation,
} from "../../../../../../utils/scoringExplanationCache";

const token = Cookies.get("authToken");
const officeCode = Cookies.get("officeCode");

const eq = (a, b) =>
  (a || "").toString().toUpperCase() === (b || "").toString().toUpperCase();

const compareCarriersByName = (a, b) =>
  (a.carrierName || a.carrierCode || "").localeCompare(
    b.carrierName || b.carrierCode || ""
  );

/** RankCost order: backend rank first, then lowest KPI-Cost (totalPrice). */
const compareCarriersByRank = (a, b) => {
  const rankA = Number(a?.rank);
  const rankB = Number(b?.rank);
  if (Number.isFinite(rankA) && Number.isFinite(rankB) && rankA !== rankB) {
    return rankA - rankB;
  }
  if (Number.isFinite(rankA) && !Number.isFinite(rankB)) return -1;
  if (!Number.isFinite(rankA) && Number.isFinite(rankB)) return 1;

  const kpiCostA = Number(a?.totalPrice);
  const kpiCostB = Number(b?.totalPrice);
  if (Number.isFinite(kpiCostA) && Number.isFinite(kpiCostB) && kpiCostA !== kpiCostB) {
    return kpiCostA - kpiCostB;
  }

  return compareCarriersByName(a, b);
};

const RatesShoppingModal = ({
  onClose,
  showModal,
  origin,
  originType,
  destination,
  destinationType,
  originAddress,
  destinationAddress,
  pickupDate,
  strategy,
  shipmentId,
  shipmentNumber,
  fetchShipmentDetails,
  stops,
  shipmentDetails,
  totalMiles,
  totalMinutes,
    officeCode
}) => {
  const [selectedCarrier, setSelectedCarrier] = useState(null); // {code, name}
  const [expandedCarrier, setExpandedCarrier] = useState("");
  const [rateShopping, setRateShopping] = useState([]);
  const [rankedCarriers, setRankedCarriers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [initialAssignedCarrier, setInitialAssignedCarrier] = useState(null);
  const [rejectedCarriers, setRejectedCarriers] = useState([]);
  const [recalledCarriers, setRecalledCarriers] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState(shipmentDetails?.paymentMethod || "PREPAID");
  const [selectionOutcomeId, setSelectionOutcomeId] = useState(null);
  const [scoringExplanation, setScoringExplanation] = useState(null);
  const [scoringExplanationLoading, setScoringExplanationLoading] = useState(false);
  const [scoreDetailsCarrier, setScoreDetailsCarrier] = useState(null);
  const [missingQuantities, setMissingQuantities] = useState([]);

  const originLabel = React.useMemo(() => {
    const pickup = (stops || []).find((s) => s.stopType === "PICKUP");
    return (pickup?.stopName || pickup?.warehouseName || "").trim() || origin || "Origin";
  }, [stops, origin]);

  const destLabel = React.useMemo(() => {
    const dropoff = (stops || []).find((s) => s.stopType === "DROP_OFF");
    return (dropoff?.stopName || dropoff?.warehouseName || "").trim() || destination || "Destination";
  }, [stops, destination]);

  // ---------- utils ----------
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const kmToMiles = (km) => (km * 0.621371192).toFixed(4);
  const getId = (x) =>
    (x?.carrierCode || x?.carrierName || "").toString().toUpperCase();

const buildRows = (items = []) =>
  (items || []).map((it) => ({
    type: it.type, // BASE, FSC, etc.
    basis: it.basis || "N/A",
    cost: it.cost ?? 0,
    price: it.price ?? 0,
    currency: it.currency?.currencyCode || "USD",
    rateCarrierCode: it.rateCarrierCode || "—",
  }));

const formatMoney = (value, currency = "USD") => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return `$${n.toFixed(2)} ${currency}`;
};

const formatAllocationPct = (value) => {
  if (value == null || value === "") return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return `${Number.isInteger(n) ? n : n.toFixed(1)}%`;
};

  // Prefer top-level arrays, fallback to route segment / breakdowns
  const extractCarrierCode = (result) => {
    const upper = (v) => (v ?? "").toString().toUpperCase();

    if (Array.isArray(result?.routeCarrierCodes) && result.routeCarrierCodes.length) {
      return upper(result.routeCarrierCodes[0]);
    }
    if (Array.isArray(result?.rateCarrierCodes) && result.rateCarrierCodes.length) {
      return upper(result.rateCarrierCodes[0]);
    }

    const seg = result?.pathSegments?.[0];
    if (seg?.routeCarrierCode) return upper(seg.routeCarrierCode);

    const pickBreakCode = (arr) =>
      (arr || []).find((b) => b?.rateCarrierCode)?.rateCarrierCode;

    const rr = seg?.routeRate;
    const fromBreakdowns =
      pickBreakCode(rr?.rateBreakdown) ||
      pickBreakCode(rr?.accBreakdown) ||
      pickBreakCode(rr?.adjustedBreakdown);

    return upper(fromBreakdowns);
  };

  const injectFlatBaseIfMissing = (adjustedList = [], rateList = []) => {
    const normalized = (s) => (s ?? "").toString().toUpperCase();
    const hasBase = adjustedList.some(
      (b) => normalized(b.type) === "BASE"
    );
    if (hasBase) return [...adjustedList];

    const flatBase = rateList.find(
      (b) =>
        normalized(b.type) === "BASE" &&
        normalized(b.basis) === "FLAT"
    );
    return flatBase ? [...adjustedList, flatBase] : [...adjustedList];
  };

  // ---------- API fetchers ----------
  const fetchRateShopping = async (retry = 0) => {
    try {
      const freshToken = Cookies.get("authToken");
      // this is the single office code on cookies should not use this
      const response = await httpClient.post(
        `/api/organizations/${officeCode}/rate-shopping/all-paths`,
        {},
        {
          params: {
            origin,
            destination,
            originType:  "GEOGRAPHY",
            destinationType: "GEOGRAPHY",
            operationDate: pickupDate,
            strategy: strategy || "ALL",
            totalMiles: totalMiles ?? undefined,
            totalMinutes: totalMinutes ?? undefined,
            originAddress: retry === 0 ? originAddress : null,
            destinationAddress: retry === 0 ? destinationAddress : null,
            paymentMethod,
            shipmentNumber: shipmentNumber ?? undefined
          },
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${freshToken}`,
            "Office-Code": officeCode,
          },
        }
      );
      // Backend may return { message } on empty graph, or { ranked_carriers, route_solutions }
      const parsed = parseRateShoppingResponse(response.data, {
        extractOutcomeId: extractSelectionOutcomeId,
      });
      if (response.data?.message && !parsed.paths.length && !parsed.rankedCarriers.length) {
        setRateShopping([]);
        setRankedCarriers([]);
        setSelectionOutcomeId(null);
        return;
      }
      setRankedCarriers(parsed.rankedCarriers);
      setRateShopping(parsed.paths);
      setSelectionOutcomeId(parsed.outcomeId);
     } catch (error) {
    //    // ⭐ ON ERROR, CLEAR UI LIST
    //   setRateShopping([]);

    //   if (retry < 2) {
    //     setTimeout(() => fetchRateShopping(retry + 1), 1500 * (retry + 1));
    //   } else {
    //     console.error("Final error after retries:", error);
    //     setIsLoading(false);
    //   }
    // } finally {
    //   setIsLoading(false);
    // }
   // catch (error) {
  setRateShopping([]);
  setRankedCarriers([]);
  setSelectionOutcomeId(null);
  console.error("Error:", error);
} finally {
  setIsLoading(false);
}

  };

  const scoringFetchKeyRef = useRef("");

  useEffect(() => {
    if (!showModal) {
      setScoreDetailsCarrier(null);
      scoringFetchKeyRef.current = "";
    }
  }, [showModal]);

  const resolveSelectionOutcomeId = useCallback(async () => {
    if (selectionOutcomeId) return selectionOutcomeId;
    if (!shipmentId) return null;

    const scope = { originatorId: officeCode, originatorCode: officeCode };

    try {
      const listResponse = await resourceSelectionApi.listShipmentSelectionOutcomes(
        shipmentId,
        scope
      );
      const fromList = pickLatestSelectionOutcomeId(listResponse.data);
      if (fromList) {
        setSelectionOutcomeId(fromList);
        return fromList;
      }
    } catch (error) {
      console.warn("Unable to list selection outcomes:", error);
    }

    try {
      const selectResponse = await resourceSelectionApi.selectShipmentById(
        shipmentId,
        { skip_rate_shopping: true, skip_shipment_tender: true },
        officeCode ? { "Office-Code": officeCode } : {}
      );
      const fromSelect = extractSelectionOutcomeId(selectResponse.data);
      if (fromSelect) {
        setSelectionOutcomeId(fromSelect);
        return fromSelect;
      }
    } catch (error) {
      console.warn("Unable to resolve selection outcome via select:", error);
    }

    return null;
  }, [shipmentId, selectionOutcomeId, officeCode]);

  const ensureRankedCarriersOnOutcome = useCallback(
    async (outcomeId) => {
      if (!shipmentId || !outcomeId || !rankedCarriers.length) return false;

      const scope = { originatorId: officeCode, originatorCode: officeCode };
      const carriers = rankedCarriers
        .map((row) => ({
          carrier_code: (row?.carrier_code || row?.carrierCode || "").toString().trim(),
          score: row?.score ?? null,
          kpi_factor: row?.kpi_factor ?? row?.kpiFactor ?? null,
          cost: row?.cost ?? null,
          price: row?.price ?? null,
        }))
        .filter((row) => row.carrier_code);

      if (!carriers.length) return false;

      try {
        await resourceSelectionApi.saveRankedCarrierSnapshot(
          shipmentId,
          outcomeId,
          { carriers, verify_capacity: false, verifyCapacity: false },
          scope
        );
        invalidateScoringExplanationCache(shipmentId, outcomeId);
        return true;
      } catch (error) {
        if (error?.response?.status === 409) {
          invalidateScoringExplanationCache(shipmentId, outcomeId);
          return true;
        }
        console.warn("Unable to persist ranked carriers for scoring explanation:", error);
        return false;
      }
    },
    [shipmentId, rankedCarriers, officeCode]
  );

  const loadScoringExplanation = useCallback(async (carrierCode, { force = false } = {}) => {
    if (!shipmentId) return null;

    const outcomeId = await resolveSelectionOutcomeId();
    if (!outcomeId) return null;

    await ensureRankedCarriersOnOutcome(outcomeId);

    const cacheScope = carrierCode ? `${outcomeId}:${carrierCode}` : outcomeId;
    const cached = getCachedScoringExplanation(shipmentId, cacheScope);
    if (!force && cached && isUsefulScoringExplanation(cached, carrierCode)) {
      setScoringExplanation(cached);
      return cached;
    }

    setScoringExplanationLoading(true);
    try {
      const response = await resourceSelectionApi.getScoringExplanation(
        shipmentId,
        outcomeId,
        {
          originatorId: officeCode,
          originatorCode: officeCode,
          carrierCode,
        }
      );
      setCachedScoringExplanation(shipmentId, cacheScope, response.data);
      setScoringExplanation(response.data);
      return response.data;
    } catch (error) {
      console.warn("Scoring explanation fetch failed:", error);
      return null;
    } finally {
      setScoringExplanationLoading(false);
    }
  }, [shipmentId, officeCode, resolveSelectionOutcomeId, ensureRankedCarriersOnOutcome]);

  const fetchRejectedCarriers = async () => {
    try {
      const freshToken = Cookies.get("authToken");
      const res = await httpClient.get(
        `/api/carrier-selection/${officeCode}/${shipmentNumber}/actions/REJECT`,
        { headers: { Authorization: `Bearer ${freshToken}`, "Office-Code": officeCode } }
      );
      setRejectedCarriers(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      if (err?.response?.status === 404) {
        setRejectedCarriers([]); // no rejects is not an error
      } else {
        console.error("Rejected fetch error:", err);
        setRejectedCarriers([]);
      }
    }
  };

  const fetchRecalledCarriers = async () => {
    try {
      const freshToken = Cookies.get("authToken");
      const res = await httpClient.get(
        `/api/carrier-selection/${officeCode}/${shipmentNumber}/actions/RECALL`,
        { headers: { Authorization: `Bearer ${freshToken}`, "Office-Code": officeCode } }
      );
      setRecalledCarriers(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      if (err?.response?.status === 404) {
        setRecalledCarriers([]); // no recalls is not an error
      } else {
        console.error("Recalled fetch error:", err);
        setRecalledCarriers([]);
      }
    }
  };

  const fetchBasisCheck = async () => {
    if (!shipmentNumber) return;
    try {
      const freshToken = Cookies.get("authToken");
      const res = await httpClient.post(
        `/api/organizations/${officeCode}/rate-shopping/check-basis`,
        {},
        {
          params: { shipmentNumber },
          headers: { Authorization: `Bearer ${freshToken}`, "Office-Code": officeCode },
        }
      );
      setMissingQuantities(res.data?.missingCostQuantities ?? []);
    } catch (err) {
      console.warn("Basis pre-check failed (non-blocking):", err);
      setMissingQuantities([]);
    }
  };

  // ---------- initial load ----------
  useEffect(() => {
            let isMounted = true;

    const fetchData = async () => {
      if (
        origin &&
        destination &&
        pickupDate &&
        Cookies.get("authToken") &&
        Cookies.get("officeCode")
      ) {
        try {
          await Promise.all([
            fetchRateShopping(),
            fetchRejectedCarriers(),
            fetchRecalledCarriers(),
            fetchBasisCheck(),
          ]);
        } catch (error) {
          console.error("Fetch error:", error);
        } finally {
          if (isMounted) setIsLoading(false);
        }
      }
    };

    fetchData();
    return () => {
      isMounted = false;
    };
  }, [origin, destination, pickupDate, showModal, paymentMethod, shipmentNumber, officeCode]);

  const carriersLoaded =
    !isLoading && (rateShopping.length > 0 || rankedCarriers.length > 0);

  useEffect(() => {
    if (!showModal || !carriersLoaded || !shipmentId) return;

    const fetchKey = `${shipmentId}:${selectionOutcomeId || "pending"}`;
    if (scoringFetchKeyRef.current === fetchKey && scoringExplanation) return;
    scoringFetchKeyRef.current = fetchKey;

    let cancelled = false;
    (async () => {
      const data = await loadScoringExplanation();
      if (!cancelled && !data) {
        console.warn("Scoring explanation not available for this shipment.");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    showModal,
    carriersLoaded,
    shipmentId,
    selectionOutcomeId,
    loadScoringExplanation,
    scoringExplanation,
  ]);

  // ---------- shape + dedupe + sort results ----------
  const rankedCarrierMap = React.useMemo(
    () => buildRankedCarrierMap(rankedCarriers),
    [rankedCarriers]
  );

  const rawResults = React.useMemo(() => {
    const pathRows = normalizeRateShoppingResults(rateShopping);
    const sourceRows =
      pathRows.length > 0
        ? pathRows
        : rankedCarriers.map((row, index) => {
            const code = (row?.carrier_code || row?.carrierCode || "")
              .toString()
              .trim()
              .toUpperCase();
            if (!code) return null;
            return {
              routeCarrierCodes: [code],
              rateCarrierCodes: [code],
              pathSegments: [],
              pathTotalCost: row?.cost ?? 0,
              adjustedPathTotalCost: row?.cost ?? 0,
              pathTotalPrice: row?.price ?? 0,
              adjustedPathTotalPrice: row?.price ?? 0,
              pathTotalTime: (Number(totalMinutes) || 0) / 60,
              __rank: row?.rank ?? index + 1,
            };
          }).filter(Boolean);

    return sourceRows
    .filter((result) => {
            const hasValidSegments =
              Array.isArray(result?.pathSegments) && result.pathSegments.length > 0;
            const hasCarrierIdentity = Boolean(extractCarrierCode(result));
            if (!hasValidSegments && !hasCarrierIdentity) {
              console.warn("Filtering out result without carrier identity:", result);
            }
            return hasValidSegments || hasCarrierIdentity;
        })
    .map((result, index) => {
      const distanceInKm = result?.pathSegments?.[0]?.route?.distanceInKm || 0;
      // No leg ever gets split for a via point (confirmed design — one edge, via point carried
      // as metadata), so it always lives on the single pathSegments[0].route, same place
      // distanceInKm comes from.
      const viaPointName = result?.pathSegments?.[0]?.route?.viaPointName || null;
      const viaPointGeoCode = result?.pathSegments?.[0]?.route?.viaPointGeoCode || null;
      const carrierCode = extractCarrierCode(result);
      const rateBreakdown = (result.pathSegments || []).flatMap(
        (s) => s.routeRate?.rateBreakdown || []
      );
      const accBreakdown = (result.pathSegments || []).flatMap(
        (s) => s.routeRate?.accBreakdown || []
      );
      const adjustedBreakdown = injectFlatBaseIfMissing(
        (result.pathSegments || []).flatMap(
          (s) => s.routeRate?.adjustedBreakdown || []
        ),
        rateBreakdown
      );
      const isCostUnresolvable = (result.pathSegments || []).some(
        (s) => s.routeRate?.costStatus === "COST_UNRESOLVABLE"
      );
      const routeMissingQuantities = isCostUnresolvable
        ? [...new Set((result.pathSegments || []).flatMap(
            (s) => s.routeRate?.missingQuantities || []
          ))]
        : [];
      //getting rate carrier name
      const rateCarrierName = result?.pathSegments?.[0]?.routeRate?.rateBreakdown?.[0]?.rateCarrierName;

      // Display name (use code as fallback if you don't have a descriptive name)
      const carrierName =
        (Array.isArray(result?.routeCarrierNames) && result.routeCarrierNames[0]) ||
        rateCarrierName || 
        (Array.isArray(result?.rateCarrierCodes) && result.rateCarrierCodes[0]) ||
        (Array.isArray(result?.routeCarrierCodes) && result.routeCarrierCodes[0]) ||
        carrierCode ||
        "N/A";

         const displayName = carrierName && carrierName !== carrierCode
            ? `${carrierName} (${carrierCode})`
            : carrierCode;

      const ranked = rankedCarrierMap.get(carrierCode);

      return {
        id: index, // will also use getId as key fallback
        carrierName,
        carrierCode,
        displayName,
        rank: ranked?.rank ?? result.__rank ?? null,
        // totalCost: Number(result.pathTotalCost || 0),
        // The shipment's own totalMinutes (real stops, already computed) — not each route's own
        // pathTotalTime, which is derived from that route's stored distance and can be wrong
        // (e.g. leftover placeholder test data) independent of the real shipment. Same value the
        // fallback branch above (line ~786) already uses when path data is missing — this just
        // applies it consistently on the main path too, rather than touching graph calculations.
        totalTime: ((Number(totalMinutes) || 0) / 60).toFixed(2),
        totalMiles: kmToMiles(distanceInKm),
        viaPointName,
        viaPointGeoCode,
        rateBreakdown,
        accBreakdown,
        adjustedBreakdown,
        totalCost: ranked?.cost ?? Number(result.adjustedPathTotalCost ?? result.pathTotalCost ?? 0),
        totalPrice:
          ranked?.price ??
          Number(result.adjustedPathTotalPrice ?? result.pathTotalPrice ?? 0),
        actualAllocationPct: ranked?.actualAllocationPct ?? null,
        originalTotalCost: Number(result.pathTotalCost ?? 0),
        originalTotalPrice: Number(result.pathTotalPrice ?? 0),
        adjustedTotalCost: Number(result.adjustedPathTotalCost ?? 0),
        adjustedTotalPrice: Number(result.adjustedPathTotalPrice ?? 0),
        isCostUnresolvable,
        routeMissingQuantities,
      };
    });
  }, [rateShopping, rankedCarriers, rankedCarrierMap, totalMinutes]);


  // Keep only the lowest KPI-Cost option per carrierCode; sort by backend rank
  const formattedResults = React.useMemo(() => {
    const byCarrier = new Map();
    for (const it of rawResults) {
      const prev = byCarrier.get(it.carrierCode);
      if (!prev ||
          (!it.isCostUnresolvable && prev.isCostUnresolvable) ||
          (it.isCostUnresolvable === prev.isCostUnresolvable && it.totalPrice < prev.totalPrice)) {
        byCarrier.set(it.carrierCode, it);
      }
    }

    const results = Array.from(byCarrier.values());
    const sortFn =
      paymentMethod === "COLLECT" ? compareCarriersByName : compareCarriersByRank;
    results.sort(sortFn);

    return results;
  }, [rawResults, paymentMethod]);

  // ---------- preselect based on shipmentDetails (current assignment) ----------
  useEffect(() => {
    if (!showModal || !shipmentDetails || !formattedResults.length) return;

    const prevCode = (
      shipmentDetails?.carrierOfficeCode?.partyOfficeCode ||
      shipmentDetails?.carrierCode ||
      ""
    ).toString().toUpperCase();

    const prevName = (shipmentDetails?.carrierName || "").toString().toUpperCase();

    const match = formattedResults.find((r) => {
      const code = (r.carrierCode || "").toString().toUpperCase();
      const name = (r.carrierName || "").toString().toUpperCase();
      return (prevCode && code === prevCode) || (prevName && name === prevName);
    });

    if (match) {
      const picked = { code: match.carrierCode, name: match.carrierName };
      setInitialAssignedCarrier(picked);
      setSelectedCarrier(picked);
      // Don't expand carrier details for COLLECT payment method
      if (shipmentDetails?.paymentMethod !== "COLLECT") {
        setExpandedCarrier(match.carrierName);
      }
    } else {
      setInitialAssignedCarrier(null);
    }
  }, [showModal, shipmentDetails, formattedResults]);

  // ---------- selection + expand handlers ----------
  const handleSelectCarrier = (carrierName, carrierCode) => {
    setSelectedCarrier({ code: carrierCode, name: carrierName });
  };

  const toggleExpandCarrier = (carrierName) => {
    setExpandedCarrier((prev) => (prev === carrierName ? "" : carrierName));
  };

  const openScoreDetails = async (carrierItem) => {
    const carrierCode = carrierItem?.carrierCode;
    const rankedRow = findRankedCarrierRow(rankedCarriers, carrierCode);

    let explanation = scoringExplanation;
    let breakdown = buildCarrierScoreBreakdown(carrierCode, rankedRow, explanation);

    if (!hasScoreBreakdownData(breakdown) && !scoringExplanationLoading) {
      const carrierExplanation = await loadScoringExplanation(carrierCode, { force: true });
      if (carrierExplanation) {
        explanation = carrierExplanation;
        breakdown = buildCarrierScoreBreakdown(carrierCode, rankedRow, carrierExplanation);
      }
    }

    if (!hasScoreBreakdownData(breakdown)) {
      notify.info({
        title: scoringExplanationLoading ? "Loading score details" : "Score details unavailable",
        message: scoringExplanationLoading
          ? "Scoring explanation is still loading. Try again in a moment."
          : "Resource selection has not produced a selection outcome for this shipment yet, so scoring explanation could not be loaded.",
      });
      return;
    }
    setScoreDetailsCarrier({ ...carrierItem, __scoreBreakdown: breakdown });
  };

  // ---------- confirm handler (assign) ----------
  const handleConfirm = async (shipmentNumber) => {
    if (!selectedCarrier) return;

    try {
      const freshToken = Cookies.get("authToken");
      const freshOfficeCode = Cookies.get("officeCode");

      // find selected result
      const selectedRate = normalizeRateShoppingResults(rateShopping).find((result) => {
        const arr = Array.isArray(result?.rateCarrierCodes)
          ? result.rateCarrierCodes
          : result?.rateCarrierCodes
          ? [result.rateCarrierCodes]
          : [];
        const carrier = arr[0];
        return (
          eq(carrier, selectedCarrier?.name) ||
          eq(carrier, selectedCarrier?.code)
        );
      });

      if (!selectedRate) throw new Error("No rate found for that carrier");

      // gather charges
      const breakdowns = selectedRate.pathSegments
        .flatMap((seg) => {
          const main = injectFlatBaseIfMissing(
            seg.routeRate?.adjustedBreakdown || [],
            seg.routeRate?.rateBreakdown || []
          ).filter(
            (b) =>
              !(
                (b?.type || "").toString().toUpperCase() === "FSC" &&
                Number(b?.cost) === 0
              )
          );
// JavaScript
            const socAll = (seg.routeRate?.accBreakdown || []).filter(
                (b) => (b?.type || "").toString().toUpperCase() === "SOC"
            );
            const acc = socAll.length ? [socAll[0]] : [];
          return [...main,...acc];
        })
        .map((b) => ({
        code: b.type,
        amount: b.cost,
        currency: b.currency?.currencyCode,
        // LGX-1136: carry the contract's basis/rate through -- without them the backend treats
        // a FLAT BASE as per-mile and stamps totalMiles as its quantity instead of 1.
        rateBasis: b.basis,
        appliedRate: b.appliedRate,
      }));

      const distinctCodes = [...new Set(breakdowns.map((b) => b.code))];

      const mappings = await Promise.all(
        distinctCodes.map((code) =>
          httpClient.get(
            `/api/code-mapping/code/${code}`,
            {
              headers: {
                Authorization: `Bearer ${freshToken}`,
                "Office-Code": freshOfficeCode,
              },
            }
          )
        )
      );

      const codeNameMap = mappings.reduce((map, res, idx) => {
        const code = distinctCodes[idx];
        const list = res.data || [];
        map[code] = list.length > 0 ? list[0].description : code;
        return map;
      }, {});

      const shipmentCharges = paymentMethod === "COLLECT" ? []: breakdowns.map(({ code, amount, currency, rateBasis, appliedRate }) => ({
        shipment: { id: shipmentId },
        amount,
        currency,
        rateBasis,
        appliedRate,
        type: code ==="SOC"? "COST_PER_STOP":"COST",
        code,
        chargeApprovalStatus: "PREAPPROVED",
        chargeName: codeNameMap[code],
      }));

      const isOcean = selectedRate.pathSegments.some(
        (seg) => seg.route.transportMode === "SEA"
      );
      const isTruck = selectedRate.pathSegments.some(
        (seg) => seg.route.transportMode === "TRUCK"
      );

      const updatedCarrier = {
        carrierName: selectedCarrier?.name,
        totalCharge: selectedRate.adjustedPathTotalCost.toString(),
        carrierCode: selectedCarrier?.code || selectedCarrier?.name,
        carrierOfficeCode: selectedCarrier?.code || selectedCarrier?.name,
        isOcean,
        isTruck,
        status: paymentMethod === "COLLECT" ? "Accepted" : "Assigned",
        // Without this, the backend's auto-insert-via-point-stop hook
        // (ShipmentAndChargesUpdateService, gated on dto.getRouteId() != null) never fires, no
        // matter how clearly the route has a via point — it never split into its own leg, so it's
        // always on this single pathSegments[0].route, same place viaPointName/distanceInKm live.
        routeId: selectedRate?.pathSegments?.[0]?.route?.routeId ?? null,

      };

      const response = await httpClient.put(
        `/api/shipment-charges-update/shipmentNumber/${shipmentNumber}/FULL_RESET`,
        {
          shipment: updatedCarrier,
          shipmentCharges,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${freshToken}`,
            "Office-Code": officeCode,
          },
        }
      );

      if (response.status === 200) {
        notify.success({
          title: paymentMethod === "COLLECT" ? "Shipment Accepted" : "Shipment Assigned",
          message: paymentMethod === "COLLECT" 
          ? `#${shipmentNumber} accepted` 
          : `#${shipmentNumber} assigned to ${selectedCarrier?.name}`,
        });
        fetchShipmentDetails();
        onClose();
      }
    } catch (error) {
      console.error("Update error:", error);
      notify.error({
        title: "Failed to Assign Carrier",
        message:
          error?.response?.data?.content ||
          error?.response?.data?.message ||
          "Something went wrong while assigning the carrier. Please try again.",
      });
    }
  };

  // ---------- derived UI state ----------
  const isSameAsInitiallyAssigned =
    initialAssignedCarrier &&
    selectedCarrier &&
    eq(initialAssignedCarrier.code, selectedCarrier.code);

  const isConfirmDisabled =
    isLoading || !selectedCarrier || (initialAssignedCarrier && isSameAsInitiallyAssigned);

  const isInitialFor = (item) =>
    !!initialAssignedCarrier &&
    (eq(initialAssignedCarrier.code, item.carrierCode) ||
      eq(initialAssignedCarrier.name, item.carrierName));

  const isSelectedFor = (item) =>
    !!selectedCarrier &&
    (eq(selectedCarrier.code, item.carrierCode) ||
      eq(selectedCarrier.name, item.carrierName));

  // Build a Set of historical carrier codes
  const historicalSet = React.useMemo(() => {
    const s = new Set();
    (rejectedCarriers || []).forEach((h) => s.add(getId(h)));
    (recalledCarriers || []).forEach((h) => s.add(getId(h)));
    return s;
  }, [rejectedCarriers, recalledCarriers]);

  // Partition formattedResults into Available vs Recalled/Rejected
  const { availableResults, recalledResults } = React.useMemo(() => {
    const available = [];
    const recalled = [];
    (formattedResults || []).forEach((it) => {
      (historicalSet.has(getId(it)) ? recalled : available).push(it);
    });
    const sortFn =
      paymentMethod === "COLLECT" ? compareCarriersByName : compareCarriersByRank;
    available.sort(sortFn);
    recalled.sort(sortFn);

    return { availableResults: available, recalledResults: recalled };
}, [formattedResults, historicalSet, paymentMethod]);

  const getCarrierStatus = (carrierItem) => {
    const id = getId(carrierItem);
    if (rejectedCarriers.some((c) => getId(c) === id)) return "Rejected";
    if (recalledCarriers.some((c) => getId(c) === id)) return "Recalled";
    return null;
  };

  

  // ---------- rendered card (single source of truth) ----------
  const renderCarrierCard = (carrierItem) => (
    <li
      key={carrierItem.id ?? getId(carrierItem)}
      className={`rounded-lg border shadow bg-white transition p-fluid-3 ${
        isSelectedFor(carrierItem) ? "border-blue-600 ring-2 ring-blue-300" : "border-gray-200"
      }`}
    >
      {/* Header */}
      <div className="flex items-center gap-fluid-2 mb-fluid-2">
        <div className="w-6 h-6 bg-blue-900 rounded-md flex items-center justify-center">
          <FaTruckFront className="text-white text-base" />
        </div>

        <h4 className="text-fluid-base font-semibold text-[#030229] flex items-center gap-fluid-1">
          {carrierItem.displayName || carrierItem.carrierName}
        </h4>
      </div>

      {/* Two-Column Layout */}
      <div className="flex flex-col md:flex-row md:items-start gap-6">
        {/* Left Column: Route & Details */}
        <div className="flex-1 border-r border-gray-200 pr-4">
          {/* Route Summary */}
          <div className="flex items-center justify-between py-fluid-2">
            <p className="font-bold text-fluid-sm text-[#030229]">
              {originLabel}
            </p>
            <MapPin className="w-4 h-4 text-[#223688]" />
            <div className="relative flex-1 mx-2 border-t-2 border-dotted border-blue-900">
              <div className="absolute left-1/2 -translate-x-1/2 -translate-y-[2.25rem] bg-[#223688] text-white text-xs font-bold px-2 py-1 rounded whitespace-nowrap">
                {carrierItem.totalTime} Hours
              </div>
              <Truck className="w-5 h-5 text-blue-900 absolute left-[25%] -top-3" />
              <Truck className="w-5 h-5 text-blue-900 absolute left-[75%] -top-3" />
              {carrierItem.viaPointName && (
                <p className="absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 bg-white px-1 font-bold text-fluid-sm text-[#030229] whitespace-nowrap">
                  {carrierItem.viaPointName}
                </p>
              )}
            </div>
            <MapPin className="w-4 h-4 text-[#223688]" />
            <p className="font-bold text-fluid-sm text-[#030229]">
              {destLabel}
            </p>
          </div>

       {/* Shipment Info */}
        <div className="mt-fluid-2">
          <div className="grid grid-cols-4 gap-x-fluid-8 gap-y-fluid-2 text-fluid-sm">
            <div>
              <p className="text-dense-xs font-semibold text-[#030229]">Mode</p>
              <p className="text-[#030229] leading-tight">{shipmentDetails?.movementType || "—"}</p>
            </div>

            <div>
              <p className="text-dense-xs font-semibold text-[#030229]">Pick up date</p>
              <p className="text-[#030229] leading-tight">
                {formatDate(shipmentDetails?.pickupDate)}
              </p>
            </div>

            <div>
              <p className="text-dense-xs font-semibold text-[#030229]">Carrier code</p>
              <p className="text-[#030229] leading-tight">
                {carrierItem.carrierCode}
              </p>
            </div>

            <div>
              <p className="text-dense-xs font-semibold text-[#030229]">Total distance</p>
              <p className="text-[#030229] leading-tight">
                {shipmentDetails?.totalMiles != null
                  ? `${Number(shipmentDetails.totalMiles).toFixed(1)} miles`
                  : carrierItem.totalMiles
                    ? `${carrierItem.totalMiles} miles`
                    : "N/A"}
              </p>
            </div>
          </div>
        </div>


          <div
            className={`overflow-hidden transition-all duration-500 ease-in-out ${
              expandedCarrier === carrierItem.carrierName
                ? "max-h-[1000px] opacity-100"
                : "max-h-0 opacity-0"
            }`}
          >
            <div className="mt-fluid-4 pt-fluid-3 border-t border-dashed border-gray-300 space-y-fluid-4 text-fluid-sm text-[#030229]">
              {/* Price / KPI-Cost Breakdown */}
              <div>
                <p className="font-semibold mb-fluid-1">Price / KPI-Cost Breakdown</p>
                <div className="grid grid-cols-3 gap-x-fluid-4 text-dense-xs font-semibold text-gray-600 mb-1">
                  <span />
                  <span className="text-right">Price</span>
                  <span className="text-right">KPI-Cost</span>
                </div>
                <ul className="space-y-1 text-fluid-sm">
                  <li className="grid grid-cols-3 gap-x-fluid-4 items-center">
                    <span>Base</span>
                    <span className="text-right tabular-nums">
                      {formatMoney(
                        carrierItem.adjustedBreakdown.find((b) => b.type === "BASE")?.cost ?? 0
                      )}
                    </span>
                    <span className="text-right tabular-nums">
                      {formatMoney(
                        carrierItem.adjustedBreakdown.find((b) => b.type === "BASE")?.price ?? 0
                      )}
                    </span>
                  </li>
                  <li className="grid grid-cols-3 gap-x-fluid-4 items-center">
                    <span>FSC</span>
                    <span className="text-right tabular-nums">
                      {formatMoney(
                        carrierItem.adjustedBreakdown.find((b) => b.type === "FSC")?.cost ?? 0
                      )}
                    </span>
                    <span className="text-right tabular-nums">
                      {formatMoney(
                        carrierItem.adjustedBreakdown.find((b) => b.type === "FSC")?.price ?? 0
                      )}
                    </span>
                  </li>
                  <li className="grid grid-cols-3 gap-x-fluid-4 items-center font-semibold border-t border-gray-200 pt-1 mt-1">
                    <span>Total</span>
                    <span className="text-right tabular-nums">
                      {formatMoney(carrierItem.adjustedTotalCost)}
                    </span>
                    <span className="text-right tabular-nums">
                      {formatMoney(carrierItem.adjustedTotalPrice)}
                    </span>

                  </li>
                </ul>

                <div className="mt-fluid-4 space-y-fluid-4">
                  <div>
                    <p className="font-semibold mb-fluid-1">Estimated Rates</p>
                    <Table1
                      headers={["Code", "Basis", "Price (USD)", "KPI-Cost (USD)"]}
                      data={buildRows(carrierItem.adjustedBreakdown)}
                      renderRow={(row) => (
                        <>
                          <td className="px-fluid-3 py-fluid-2 text-fluid-sm">{row.type}</td>
                          <td className="px-fluid-3 py-fluid-2 text-fluid-sm">{row.basis}</td>
                          <td className="px-fluid-3 py-fluid-2 text-fluid-sm tabular-nums">
                            ${Number(row.cost || 0).toFixed(2)}
                          </td>
                          <td className="px-fluid-3 py-fluid-2 text-fluid-sm tabular-nums">
                            ${Number(row.price || 0).toFixed(2)}
                          </td>
                        </>
                      )}
                    />
                  </div>

                  {/* Accessorials */}
                  <div>
                    <p className="font-semibold mb-fluid-1">Accessorials</p>
                    <Table1
                      headers={["Code", "Basis", "Price (USD)", "KPI-Cost (USD)"]}
                      data={buildRows(carrierItem.accBreakdown)}
                      renderRow={(row) => (
                        <>
                          <td className="px-fluid-3 py-fluid-2 text-fluid-sm">{row.type}</td>
                          <td className="px-fluid-3 py-fluid-2 text-fluid-sm">{row.basis}</td>
                          <td className="px-fluid-3 py-fluid-2 text-fluid-sm tabular-nums">
                            ${Number(row.cost || 0).toFixed(2)}
                          </td>
                          <td className="px-fluid-3 py-fluid-2 text-fluid-sm tabular-nums">
                            ${Number(row.price || 0).toFixed(2)}
                          </td>
                        </>
                      )}
                    />
                  </div>

                  {/* Adjusted Rates */}
                  {/* <div>
                    <p className="font-semibold mb-fluid-1">Main Rates</p>
                    <Table1
                      headers={["Code", "Basis", "Price (USD)"]}
                      data={buildRows(carrierItem.rateBreakdown)}
                      renderRow={(row) => (
                        <>
                          <td className="px-fluid-3 py-fluid-2 text-fluid-sm">{row.type}</td>
                          <td className="px-fluid-3 py-fluid-2 text-fluid-sm">{row.basis}</td>
                          <td className="px-fluid-3 py-fluid-2 text-fluid-sm">
                            ${Number(row.cost || 0).toFixed(2)}
                          </td>
                        </>
                      )}
                    />
                  </div> */}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column: % Assigned / Price / KPI-Cost + Actions */}
        <div className="w-full md:w-80 flex flex-col items-center md:items-end justify-center mt-4 md:mt-0">
          {shipmentDetails?.paymentMethod !== "COLLECT" && (
            <div className="grid grid-cols-3 gap-x-fluid-3 w-full mb-fluid-2 text-center md:text-right">
              <div>
                <p className="text-dense-xs font-semibold text-gray-600 uppercase tracking-wide whitespace-nowrap">
                  % Assigned
                </p>
                <p className="text-fluid-lg font-bold text-[#223688] tabular-nums">
                  {formatAllocationPct(carrierItem.actualAllocationPct)}
                </p>
              </div>
              <div>
                <p className="text-dense-xs font-semibold text-gray-600 uppercase tracking-wide">
                  Price
                </p>
                {carrierItem.isCostUnresolvable ? (
                  <span className="inline-block mt-0.5 rounded-full bg-amber-100 border border-amber-300 px-2 py-0.5 text-xs font-semibold text-amber-800 whitespace-nowrap">
                    Needs {(carrierItem.routeMissingQuantities?.[0]) || "quantity"}
                  </span>
                ) : (
                  <p className="text-fluid-lg font-bold text-[#223688] tabular-nums">
                    {formatMoney(carrierItem.totalCost)}
                  </p>
                )}
              </div>
              <div>
                <p className="text-dense-xs font-semibold text-gray-600 uppercase tracking-wide">
                  KPI-Cost
                </p>
                <p className="text-fluid-lg font-bold text-[#223688] tabular-nums">
                  {formatMoney(carrierItem.totalPrice)}
                </p>
              </div>
            </div>
          )}

          <button
            type="button"
            disabled={isInitialFor(carrierItem) || carrierItem.isCostUnresolvable}
            onClick={(e) => {
              e.stopPropagation();
              if (!isInitialFor(carrierItem) && !carrierItem.isCostUnresolvable) {
                handleSelectCarrier(carrierItem.carrierName, carrierItem.carrierCode);
              }
            }}
            className={`px-fluid-3 py-fluid-2 rounded-md text-fluid-base font-semibold transition
              ${isInitialFor(carrierItem)
                ? "bg-[#223688] text-white cursor-not-allowed"
                : carrierItem.isCostUnresolvable
                  ? "bg-amber-200 text-amber-800 cursor-not-allowed"
                  : "bg-[#223688] text-white hover:bg-[#1a2d66]"}`}
          >
            {isInitialFor(carrierItem) ? "Assigned" : carrierItem.isCostUnresolvable ? "Unavailable" : "Select"}
          </button>
          {shipmentDetails?.paymentMethod !== "COLLECT" && (
            <div className="mt-fluid-1.5 grid grid-cols-3 gap-x-fluid-3 w-full text-fluid-sm text-center md:text-right">
              <div />
              <div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleExpandCarrier(carrierItem.carrierName);
                  }}
                  className="text-[#223688] underline"
                >
                  {expandedCarrier === carrierItem.carrierName ? "Hide details" : "View details"}
                </button>
              </div>
              <div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    openScoreDetails(carrierItem);
                  }}
                  disabled={scoringExplanationLoading && !scoringExplanation}
                  className="text-[#223688] underline disabled:cursor-wait disabled:opacity-60"
                >
                  Score details
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </li>
  );

  // ---------- loading ----------
  if (isLoading) {
    return (
      <FullScreenSlideUpModal isOpen={showModal} onClose={onClose}>
        <div className="w-full text-center p-fluid-6">
          <p className="text-fluid-lg font-semibold text-blue-600 mb-fluid-4">Loading Carriers</p>
          <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
        </div>
      </FullScreenSlideUpModal>
    );
  }

  const scoreDetailsOverlay = (
    <CarrierScoreDetailsModal
      carrier={scoreDetailsCarrier}
      onClose={() => setScoreDetailsCarrier(null)}
    >
      {scoreDetailsCarrier?.__scoreBreakdown
        ? undefined
        : renderScoringExplanationBody(
            scoringExplanation,
            scoreDetailsCarrier?.carrierCode,
            findRankedCarrierRow(rankedCarriers, scoreDetailsCarrier?.carrierCode)
          )}
    </CarrierScoreDetailsModal>
  );

  // ---------- render ----------
  return (
    <>
    <FullScreenSlideUpModal isOpen={showModal} onClose={onClose}>
      {/* Shipment Overview */}
     <div className="sticky top-0 z-50 bg-custom-bg-light-purple pb-fluid-2 -mx-fluid-3 px-fluid-2">
       <div className="grid grid-cols-1 sm:grid-cols-4 lg:grid-cols-4 gap-fluid-3">

        <div className="bg-white rounded-2xl shadow px-fluid-3 py-fluid-2">
          <p className="text-dense-xs text-[#030229] font-semibold mb-1">Buyer</p>
          <p className="text-fluid-sm text-[#030229]">{shipmentDetails?.receiverName || "N/A"}</p>
        </div>
        <div className="bg-white rounded-2xl shadow px-fluid-3 py-fluid-2">
          <p className="text-dense-xs text-[#030229] font-semibold mb-1">Created Date</p>
          <p className="text-fluid-sm text-[#030229]">
            {shipmentDetails?.createdDate
              ? new Date(shipmentDetails.createdDate).toLocaleDateString()
              : "N/A"}
          </p>
        </div>
        <div className="bg-white rounded-2xl shadow px-fluid-3 py-fluid-2">
          <p className="text-dense-xs text-[#030229] font-semibold mb-1">Shipment Number</p>
          <p className="text-fluid-sm text-[#030229]">{shipmentDetails?.shipmentNumber || "N/A"}</p>
        </div>
        <div className="bg-white rounded-2xl shadow px-fluid-3 py-fluid-2">
          <p className="text-dense-xs text-[#030229] font-semibold mb-1">Order Value</p>
          <p className="text-fluid-sm text-[#030229]">${shipmentDetails?.orderValue || 0}</p>
        </div>
      </div>

      {/* Route Details */}
      <div className="grid grid-cols-1 sm:grid-cols-5 lg:grid-cols-5 gap-fluid-3 mt-fluid-2 items-end mb-fluid-2">
        <div>
          <p className="text-dense-xs text-[#030229] font-semibold mb-1">Origin</p>
          <input
            value={origin || ""}
            readOnly
            className="bg-white border px-3 py-2 rounded w-full text-fluid-sm text-[#030229]"
          />
        </div>
        <div>
          <p className="text-dense-xs text-[#030229] font-semibold mb-1">Destination</p>
          <input
            value={destination || ""}
            readOnly
            className="bg-white border px-3 py-2 rounded w-full text-fluid-sm text-[#030229]"
          />
        </div>
        <div className="relative">
          <p className="text-dense-xs text-[#030229] font-semibold mb-1">Pick up date</p>
          <input
            value={formatDate(shipmentDetails?.pickupDate)}
            readOnly
            className="bg-white border px-3 py-2 rounded w-full text-fluid-sm text-[#030229]"
          />
        </div>
        <div className="relative">
          <p className="text-dense-xs text-[#030229] font-semibold mb-1">Delivery date</p>
          <input
            value={formatDate(shipmentDetails?.deliveryDate)}
            readOnly
            className="bg-white border px-3 py-2 rounded w-full text-fluid-sm text-[#030229]"
          />
        </div>
         <div>
            <p className="text-dense-xs text-[#030229] font-semibold mb-1">Payment Method</p>
            <select
              value={paymentMethod}
              disabled
              className="bg-[#f5f5f5] border px-3 py-2 rounded w-full text-fluid-sm text-[#030229] cursor-not-allowed pointer-events-none">
              <option value="PREPAID">Prepaid</option>
              <option value="COLLECT">Collect</option>
            </select>
          </div>

        <div>
          <p className="text-dense-xs text-[#030229] font-semibold mb-1">Mode</p>
          <input
            value={shipmentDetails?.movementType || ""}
            readOnly
            className="bg-white border px-3 py-2 rounded w-full text-fluid-sm text-[#030229]"
          />
        </div>
      </div>
      </div>

      {/* Available Carriers */}
      <section aria-labelledby="available-carriers-heading">
        <div className="flex items-center justify-between">
          <h3 id="available-carriers-heading" className="text-fluid-lg font-semibold text-[#030229]">
            Available carriers
          </h3>
          {paymentMethod !== "COLLECT" && availableResults.length > 0 && (
            <div className="hidden md:grid grid-cols-3 gap-x-fluid-3 w-80 text-dense-xs font-semibold text-gray-600 uppercase tracking-wide text-center">
              <span className="whitespace-nowrap">% Assigned</span>
              <span>Price</span>
              <span>KPI-Cost</span>
            </div>
          )}
        </div>

        {missingQuantities.length > 0 && (
          <div className="mt-fluid-2 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-fluid-3 py-fluid-2 text-fluid-sm text-amber-800">
            <span className="mt-0.5 shrink-0">⚠</span>
            <span>
              Some routes cannot be priced because this shipment is missing{" "}
              {missingQuantities.map((q) => q.label).join(", ")}.{" "}
              Add the missing quantities to the shipment items to enable all routes.
            </span>
          </div>
        )}

        <div className="my-fluid-2">
          {availableResults.length > 0 ? (
            <ul className="space-y-fluid-3">
              {availableResults.map((carrierItem) => renderCarrierCard(carrierItem))}
            </ul>
          ) : (
            <p className="text-gray-500 text-center py-fluid-1">No eligible carriers. Please select from the options in the Rejected/Recall section below.</p>
          )}
        </div>
      </section>

      {/* Previously rejected/recalled Carriers (same card UI) */}
      <section className="my-fluid-1" aria-labelledby="historical-carriers-heading">
        <div className="flex items-center justify-between mb-1">
          <h3 id="historical-carriers-heading" className="text-fluid-lg font-semibold text-[#030229]">
            Previously rejected/recalled carriers
          </h3>
        </div>

        <div className="my-fluid-1">
          {recalledResults.length > 0 ? (
            <ul className="space-y-fluid-3">
              {recalledResults.map((carrierItem) => renderCarrierCard(carrierItem))}
            </ul>
          ) : (
            <p className="text-gray-500 text-center py-fluid-4">No rejected or recalled carriers</p>
          )}
        </div>
      </section>

      {/* Footer */}
      <div className="sticky bottom-0 z-50 bg-custom-bg-light-purple border-t border-gray-200 flex justify-end gap-fluid-1 -px-fluid-3 sm:px-fluid-3 py-fluid-2 pr-fluid-4">
        <div className="flex gap-fluid-1 translate-x-[0.5rem] sm:translate-x-[1rem]">
          <button
            onClick={onClose}
            className="px-fluid-button-x py-fluid-button-y border border-gray-300 rounded text-gray-700 hover:bg-gray-100 text-fluid-sm transition"
          >
            Cancel
          </button>
          <button
            onClick={() => handleConfirm(shipmentNumber)}
            disabled={isConfirmDisabled}
            className={`px-fluid-button-x py-fluid-button-y rounded text-white text-fluid-sm font-semibold transition ${
              !isConfirmDisabled
                ? 'bg-blue-600 hover:bg-blue-700'
                : 'bg-blue-300 cursor-not-allowed'
            }`}
          >
            {isLoading ? 'Processing...' : 'Confirm'}
          </button>
        </div>
      </div>    



    </FullScreenSlideUpModal>
    {scoreDetailsOverlay}
    </>
  );
};

export default RatesShoppingModal;
